const pad = (value) => String(value).padStart(2, '0');

const validDate = (date) =>
    date instanceof Date && Number.isFinite(date.getTime());

const parseHour = (value) => {
    const match = String(value ?? '').match(/^(\d{1,2})(?::(\d{1,2}))?/);
    if (!match) return [0, 0];

    const hour = Number(match[1]);
    const minute = Number(match[2] || 0);
    return hour < 24 && minute < 60 ? [hour, minute] : [0, 0];
};

export const parseScheduleDate = (row) => {
    const value = row?.date;
    if (validDate(value)) return new Date(value.getTime());

    const match = String(value ?? '').match(
        /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s](\d{1,2})(?::(\d{1,2}))?)?/
    );
    if (!match) return null;

    const time = row?.hour !== undefined
        ? parseHour(row.hour)
        : [Number(match[4] || 0), Number(match[5] || 0)];
    const date = new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
        time[0],
        time[1]
    );

    return validDate(date) &&
        date.getFullYear() === Number(match[1]) &&
        date.getMonth() === Number(match[2]) - 1 &&
        date.getDate() === Number(match[3])
        ? date
        : null;
};

const toDay = (value) => {
    const date = validDate(value) ? new Date(value.getTime()) : new Date(value);
    if (!validDate(date)) return null;
    date.setHours(0, 0, 0, 0);
    return date;
};

export const resolveForecastRange = (periodValue, schedules = []) => {
    const campaignStart = toDay(periodValue?.[0]);
    const campaignEnd = toDay(periodValue?.[1]);
    if (campaignStart && campaignEnd) return [campaignStart, campaignEnd];

    const dates = schedules.map(parseScheduleDate).filter(Boolean);
    if (!dates.length) return null;

    return [
        toDay(new Date(Math.min(...dates.map((date) => date.getTime())))),
        toDay(new Date(Math.max(...dates.map((date) => date.getTime())))),
    ];
};

export const filterSchedulesByRange = (schedules = [], range) => {
    const start = toDay(range?.[0]);
    const end = toDay(range?.[1]);
    if (!start || !end) return [];
    end.setHours(23, 59, 59, 999);

    return schedules.filter((row) => {
        const date = parseScheduleDate(row);
        return date && date >= start && date <= end;
    });
};

const dateKey = (date) =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const aggregateForecastSchedules = (schedules = [], range) => {
    const start = toDay(range?.[0]);
    const end = toDay(range?.[1]);
    if (!start || !end) return [];

    const byHour = start.getTime() === end.getTime();
    end.setHours(23, 59, 59, 999);
    const buckets = new Map();

    schedules.forEach((row) => {
        const date = parseScheduleDate(row);
        if (!date || date < start || date > end) return;

        const day = dateKey(date);
        const key = byHour ? `${day} ${pad(date.getHours())}:00` : day;
        const current = buckets.get(key) || {
            label: key,
            timestamp: byHour
                ? new Date(
                      date.getFullYear(),
                      date.getMonth(),
                      date.getDate(),
                      date.getHours()
                  ).getTime()
                : new Date(
                      date.getFullYear(),
                      date.getMonth(),
                      date.getDate()
                  ).getTime(),
            spent: 0,
            impacts: 0,
        };

        current.spent += Number(row.costs) || 0;
        current.impacts += Number(row.totalImpacts) || 0;
        buckets.set(key, current);
    });

    return Array.from(buckets.values()).sort(
        (left, right) => left.timestamp - right.timestamp
    );
};
